// Utilities for normalizing days/times and resolving TemplateType

export const MODE_CARDS = [
  {
    value: 'update',
    title: 'Update Existing',
    blurb: 'Update schema conditions on existing fridges now.',
  },
  {
    value: 'create',
    title: 'Create New',
    blurb: 'Add new templates from CSV, including replica base prices.',
  },
  {
    value: 'schedule',
    title: 'Schedule Change',
    blurb:
      'Queue an update to schema conditions for a future date + time. Nothing applies until goLive.',
  },
];

const BASE_COLUMNS = [
  { name: 'schemaId', type: 'required' },
  { name: 'schemaType', type: 'required' },
  { name: 'discount', type: 'required' },
  { name: 'sharedSubsidyClientDiscount', type: 'optional' },
  { name: 'discountSchedule', type: 'required' },
  { name: 'discountUI', type: 'required' },
  { name: 'expiringDays', type: 'required' },
  { name: 'priceDisplayType', type: 'required' },
  { name: 'platform', type: 'required' },
];

export const COLUMNS_BY_MODE = {
  update: {
    label: 'CSV columns · Update Existing',
    cols: [...BASE_COLUMNS, { name: 'sleepScreenId', type: 'optional' }],
  },
  create: {
    label: 'CSV columns · Create New',
    cols: [
      ...BASE_COLUMNS,
      { name: 'replicaSchemaIdForBasePrices', type: 'new' },
      { name: 'sleepScreenId', type: 'optional' },
    ],
  },
  schedule: {
    label: 'CSV columns · Schedule Change',
    cols: [
      ...BASE_COLUMNS,
      { name: 'goLiveDateTime', type: 'new' },
      { name: 'sleepScreenId', type: 'optional' },
    ],
  },
};

const CANONICAL_DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DAY_SYNONYMS = {
  sun: 'Sun',
  sunday: 'Sun',
  mon: 'Mon',
  monday: 'Mon',
  tue: 'Tue',
  tues: 'Tue',
  tuesday: 'Tue',
  wed: 'Wed',
  weds: 'Wed',
  wednesday: 'Wed',
  thu: 'Thu',
  thur: 'Thu',
  thurs: 'Thu',
  thursday: 'Thu',
  fri: 'Fri',
  friday: 'Fri',
  sat: 'Sat',
  saturday: 'Sat',
};

export function normalizeDays(dayString) {
  if (!dayString || typeof dayString !== 'string')
    return { normalized: '', errors: ['Invalid day string'] };
  const tokens = dayString
    .split(/[,;]+/)
    .map((t) => t.trim())
    .filter(Boolean);

  const normalizedSet = new Set();
  const errors = [];
  tokens.forEach((token) => {
    const key = token.toLowerCase();
    const label = DAY_SYNONYMS[key];
    if (!label) {
      errors.push(`Unknown day: ${token}`);
      return;
    }
    normalizedSet.add(label);
  });

  const ordered = CANONICAL_DAY_LABELS.filter((d) => normalizedSet.has(d));
  return { normalized: ordered.join(', '), errors };
}

export function isValidTimeHHMM(value) {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(value || '');
}

export function validateEnum(value, allowed) {
  return allowed.includes(value);
}

export function toInt(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

const ALLOWED_PRICE_DISPLAY = ['EXPLICIT', 'IMPLICIT'];

export function resolveTemplateType({ schemaType, discountUI }) {
  const raw = String(schemaType || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');

  const MAP = {
    standard: 'Standard',
    expiring: 'ExpiringDiscount',
    expiringdiscount: 'ExpiringDiscount',
    subsidy: 'SubsidyDiscount',
    subsidydiscount: 'SubsidyDiscount',
    discount: 'Discount',
    mobilediscount: 'Discount',
    sharedsubsidy: 'SharedSubsidy',
  };

  const TIMEBOUND = new Set([
    'timebound',
    'timebounddiscount',
    'tmebound',
    'tmebounddiscount',
  ]);
  const TIMEBOUND_UI = new Set([
    'timebounddiscountui',
    'timeboundui',
    'tmebounddiscountui',
    'tmeboundui',
  ]);

  if (TIMEBOUND_UI.has(raw)) return 'TimeBoundDiscountUi';
  if (TIMEBOUND.has(raw))
    return discountUI ? 'TimeBoundDiscountUi' : 'TimeBoundDiscount';
  return MAP[raw] || null;
}

export function buildUpdateInputRow(csvRow, mode = 'update') {
  const {
    schemaId,
    schemaType,
    discount,
    priceDisplayType,
    platform,
    discountUI,
    replicaSchemaIdForBasePrices,
    sharedSubsidyClientDiscount,
    sleepScreenId,
  } = csvRow;
  const errors = [];
  const warnings = [];

  const trimmedSchemaId = String(schemaId ?? '').trim();
  const trimmedSchemaType = String(schemaType ?? '').trim();

  if (!trimmedSchemaId) errors.push('schemaId is required');
  if (!trimmedSchemaType) errors.push('schemaType is required');

  if (!trimmedSchemaId || !trimmedSchemaType) {
    return {
      input: { schemaId: trimmedSchemaId, templateType: null },
      errors,
    };
  }

  const templateType = resolveTemplateType({ schemaType, discountUI });
  const normalized = { schemaId, templateType };

  // Add replicaSchemaIdForBasePrices for Create mode
  if (mode === 'create' && replicaSchemaIdForBasePrices) {
    normalized.replicaSchemaIdForBasePrices = String(
      replicaSchemaIdForBasePrices,
    ).trim();
  }
  // Normalize platform once for potential use inside nested blocks only
  const p = (platform || '').toUpperCase();

  // Conditional priceDisplayType validation
  if (
    [
      'Discount',
      'SubsidyDiscount',
      'ExpiringDiscount',
      'TimeBoundDiscount',
      'TimeBoundDiscountUi',
      'SharedSubsidy',
    ].includes(templateType) &&
    !ALLOWED_PRICE_DISPLAY.includes(priceDisplayType)
  ) {
    errors.push('priceDisplayType must be EXPLICIT or IMPLICIT');
  }

  // Validate discount only for specific types
  if (
    [
      'Discount',
      'SubsidyDiscount',
      'ExpiringDiscount',
      'TimeBoundDiscount',
      'TimeBoundDiscountUi',
    ].includes(templateType)
  ) {
    const discountValue = parseFloat(discount);
    if (isNaN(discountValue) || discountValue < 1 || discountValue > 100) {
      errors.push('discount must be a number between 1 and 100');
    } else {
      // Assign discountValue to normalized object only if valid
      if (templateType === 'Discount') {
        normalized.discount = {
          discount: discountValue,
          priceDisplayType: priceDisplayType,
          platform: p === 'MOBILE' ? p : undefined,
        };
      } else if (templateType === 'SubsidyDiscount') {
        normalized.subsidy = {
          discount: discountValue,
          priceDisplayType: priceDisplayType,
        };
      } else if (templateType === 'ExpiringDiscount') {
        const days = toInt(csvRow.expiringDays);
        if (days === null || days <= 0) {
          errors.push('expiringDays must be a positive integer');
        } else {
          normalized.expiring = {
            expiringDays: days,
            discount: discountValue,
            priceDisplayType: priceDisplayType,
          };
        }
      } else if (
        templateType === 'TimeBoundDiscount' ||
        templateType === 'TimeBoundDiscountUi'
      ) {
        const schedule = String(csvRow.discountSchedule || '').trim();
        const timeWindows = [];
        if (!schedule) {
          errors.push(
            'timeBound.timeWindows is required and must be a non-empty list',
          );
        } else {
          // Split on ';' first; if none, also allow commas between blocks
          const blocks = schedule
            .split(/;+/)
            .filter(Boolean)
            .flatMap((b) => b.split(/,(?=\s*[A-Za-z])/))
            .map((b) => b.trim())
            .filter(Boolean);
          blocks.forEach((block) => {
            // Expect patterns like: Mon 15:00-17:00 or Tue 14:00–16:00
            const m = block.match(
              /^([A-Za-z]+)\s+([0-2]?\d:[0-5]\d)\s*[-–]\s*([0-2]?\d:[0-5]\d)$/,
            );
            if (!m) {
              errors.push(`Invalid time window: ${block}`);
              return;
            }
            const dayKey = m[1].toLowerCase();
            const dayLabel = DAY_SYNONYMS[dayKey];
            const startTime = m[2];
            const endTime = m[3];
            if (
              !dayLabel ||
              !isValidTimeHHMM(startTime) ||
              !isValidTimeHHMM(endTime)
            ) {
              errors.push(`Invalid time window: ${block}`);
              return;
            }
            timeWindows.push({ dayOfWeek: dayLabel, startTime, endTime });
          });
          if (timeWindows.length === 0) {
            errors.push(
              'timeBound.timeWindows is required and must be a non-empty list',
            );
          } else {
            normalized.timeBound = {
              discount: discountValue,
              priceDisplayType: priceDisplayType,
              timeWindows,
            };
          }
        }
      }
    }
  }

  // Handle SharedSubsidy template type
  if (templateType === 'SharedSubsidy') {
    const targetDiscount = parseFloat(discount);
    const clientSubsidy = parseFloat(sharedSubsidyClientDiscount);

    // Check if values are in valid range (1-100)
    const isTargetDiscountValid = targetDiscount >= 1 && targetDiscount <= 100;
    const isClientSubsidyValid = clientSubsidy >= 1 && clientSubsidy <= 100;

    // Validate targetDiscount (from discount column)
    if (!isTargetDiscountValid) {
      errors.push(
        'discount (targetDiscount) must be a number between 1 and 100',
      );
    }

    // Validate clientSubsidy (from sharedSubsidyClientDiscount column)
    if (!isClientSubsidyValid) {
      errors.push(
        'sharedSubsidyClientDiscount (clientSubsidy) must be a number between 1 and 100',
      );
    }

    // Validate that clientSubsidy is less than targetDiscount
    if (
      isTargetDiscountValid &&
      isClientSubsidyValid &&
      clientSubsidy >= targetDiscount
    ) {
      errors.push(
        'sharedSubsidyClientDiscount must be less than discount (targetDiscount)',
      );
    }

    // Build the sharedSubsidy object if all validations pass
    if (
      isTargetDiscountValid &&
      isClientSubsidyValid &&
      clientSubsidy < targetDiscount
    ) {
      normalized.sharedSubsidy = {
        targetDiscount: targetDiscount,
        clientSubsidy: clientSubsidy,
        priceDisplayType: priceDisplayType,
      };
    }
  }

  // Platform validation (do not set at root; only within discount block when valid)
  if (platform && p !== 'MOBILE') {
    errors.push('platform must be MOBILE or left blank');
  }

  // Non-blocking warnings for unused columns
  if (
    templateType !== 'ExpiringDiscount' &&
    String(csvRow.expiringDays || '').trim() !== ''
  ) {
    warnings.push('expiringDays is ignored for this template type');
  }
  if (
    !(
      templateType === 'TimeBoundDiscount' ||
      templateType === 'TimeBoundDiscountUi'
    ) &&
    String(csvRow.discountSchedule || '').trim() !== ''
  ) {
    warnings.push('discountSchedule is ignored for this template type');
  }
  if (
    templateType !== 'SharedSubsidy' &&
    String(csvRow.sharedSubsidyClientDiscount || '').trim() !== ''
  ) {
    warnings.push(
      'sharedSubsidyClientDiscount is ignored for this template type',
    );
  }

  const trimmedSleepScreenId = String(sleepScreenId || '').trim();
  if (trimmedSleepScreenId) {
    normalized.sleepScreenId = trimmedSleepScreenId;
  }

  // Attach warnings to errors array as non-blocking notes for display/download
  if (warnings.length) {
    errors.push(...warnings.map((w) => `(warning) ${w}`));
  }
  return { input: normalized, errors };
}

// Round up to the 15-min grid the server stores on, so the user sees the
// time that will actually be saved.
export function roundUpToQuarterHour(date) {
  const d = new Date(date.getTime());
  d.setSeconds(0, 0);
  const minutes = d.getMinutes();
  const remainder = minutes % 15;
  if (remainder !== 0) {
    d.setMinutes(minutes + (15 - remainder));
  }
  return d;
}

// Go-live times in the CSV are interpreted as (and displayed in) Central Time,
// regardless of the uploader's machine timezone, so a shared CSV schedules the
// same absolute instant for everyone. DST is handled by the IANA zone.
export const SCHEDULE_TIME_ZONE = 'America/Chicago';

function tzOffsetMs(utcMs, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date(utcMs));
  const map = {};
  parts.forEach((p) => {
    if (p.type !== 'literal') map[p.type] = p.value;
  });
  const hour = map.hour === '24' ? '00' : map.hour;
  const asUtc = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(hour),
    Number(map.minute),
    Number(map.second),
  );
  return asUtc - utcMs;
}

export function parseGoLiveDateTime(value) {
  const str = String(value || '').trim();
  if (!str) return { date: null, error: 'goLiveDateTime is required' };

  let y;
  let mo;
  let d;
  let h;
  let mi;

  const iso = str.match(
    /^(\d{4})-(\d{1,2})-(\d{1,2})[ T](\d{1,2}):(\d{2})(?::\d{2})?$/,
  );
  const us = str.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})[ ,]+(\d{1,2}):(\d{2})(?::\d{2})?(?:\s*([AaPp][Mm]))?$/,
  );

  if (iso) {
    [, y, mo, d, h, mi] = iso.map(Number);
  } else if (us) {
    mo = Number(us[1]);
    d = Number(us[2]);
    y = Number(us[3]);
    h = Number(us[4]);
    mi = Number(us[5]);
    if (us[6]) {
      const isPm = /p/i.test(us[6]);
      if (h === 12) h = isPm ? 12 : 0;
      else if (isPm) h += 12;
    }
  } else {
    return {
      date: null,
      error:
        'goLiveDateTime must be a date and time like 2026-07-01 14:30 or 6/30/2026 15:00 (Central)',
    };
  }

  const probe = new Date(Date.UTC(y, mo - 1, d, h, mi));
  if (
    probe.getUTCFullYear() !== y ||
    probe.getUTCMonth() !== mo - 1 ||
    probe.getUTCDate() !== d ||
    probe.getUTCHours() !== h ||
    probe.getUTCMinutes() !== mi
  ) {
    return { date: null, error: `Invalid date/time: ${str}` };
  }
  const utcGuess = Date.UTC(y, mo - 1, d, h, mi);
  const date = new Date(utcGuess - tzOffsetMs(utcGuess, SCHEDULE_TIME_ZONE));
  return { date, error: null };
}

// Scheduled-change payload is AWSJSON and sometimes double-encoded (a JSON
// string whose contents are themselves a JSON string), so parse until object.
export function parseSchedulePayload(payload) {
  let value = payload;
  let guard = 0;
  while (typeof value === 'string' && guard < 3) {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
    guard += 1;
  }
  return value && typeof value === 'object' ? value : null;
}

export function formatScheduleDateTime(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    timeZone: SCHEDULE_TIME_ZONE,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

export function buildUpdateInputFromRows(rows, mode = 'update') {
  const results = [];
  const errorsByIndex = {};
  rows.forEach((row, idx) => {
    const { input, errors } = buildUpdateInputRow(row, mode);
    if (errors.length) {
      errorsByIndex[idx] = errors;
    }
    results.push({ ...input, schemaId: input.schemaId });
  });
  return { inputs: results, errorsByIndex };
}
